import { inject, Injectable } from '@angular/core';
import { MatBottomSheetAddNewPointOrReplacePointData } from 'src/app/interfaces/mat.interface';
import { MatBottomSheetService } from 'src/app/services';
import { AddItemToCustomRouteService } from 'src/chatbot/service/chat-actions/add-item-to-custom-route.service';
import { EditedPoint, EditedStatus } from '../../../../../interfaces/edit-route.interface';
import { SuggestedPoint } from '../../../../../interfaces/edit-route.response';
import { EditRouteService } from '../../../../../services/edit-route.service';

@Injectable({
  providedIn: 'root'
})
export class CardService {
  // Service injections
  private _editRouteService = inject(EditRouteService);
  private _matBottomSheetService = inject(MatBottomSheetService);
  private _addItemToCustomRouteService = inject(AddItemToCustomRouteService);

  // Methods
  /**
   * Handles the point selection logic based on the bottom sheet data
   */
  public handlePointSelection(
    point: SuggestedPoint | EditedPoint,
    matBottomSheetData: MatBottomSheetAddNewPointOrReplacePointData
  ): void {
    if (!matBottomSheetData) return;
    
    if (matBottomSheetData.fromChatBot) {
      this.handleChatBotSelection(point, matBottomSheetData);
      return;
    }
    
    this.handleBottomSheetStatusAction(point, matBottomSheetData);
  }

  /**
   * Handles point selection from chatbot
   */
  private handleChatBotSelection(
    point: SuggestedPoint | EditedPoint,
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): void {
    const payload = this.createChatBotPayload(point, data);

    this._addItemToCustomRouteService.addItem(payload).subscribe({
      next: (res) => console.log("Response:", res),
      complete: () => this._matBottomSheetService.closeAllMatBottomSheets()
    });
  }

  /**
   * Handles the action based on the bottom sheet status
   */
  private handleBottomSheetStatusAction(
    point: SuggestedPoint | EditedPoint,
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): void {
    switch (data.status) {
      case EditedStatus.ADDED:
        this.addPoint(point as SuggestedPoint, data);
        break;
      case EditedStatus.UPDATED:
        this.replacePoint(point as SuggestedPoint, data);
        break;
      default:
        console.error('Unknown status:', data.status);
    }
  }

  /**
   * Creates the payload for adding an item from chatbot
   */
  private createChatBotPayload(
    point: SuggestedPoint | EditedPoint,
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): any {
    return {
      element_id: point?.id,
      found_name: point?.title,
      type: point?.type,
      hour: data?.startDate,
      day: 1
    };
  }

  /**
   * Validates if the required data is available for adding/replacing a point
   */
  private validateBottomSheetData(
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): { isValid: boolean; dayIndex?: number; orderIndex?: number } {
    const { dayToShowIndex, orderIndex } = data;
    
    if (dayToShowIndex === undefined || dayToShowIndex === null) {
      return { isValid: false };
    }
    
    if (orderIndex === undefined || orderIndex === null) {
      return { isValid: false };
    }
    
    return { isValid: true, dayIndex: dayToShowIndex, orderIndex };
  }

  /**
   * Adds a point to the edited custom route
   */
  public addPoint(
    point: SuggestedPoint | EditedPoint,
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): void {
    const validation = this.validateBottomSheetData(data);
    if (!validation.isValid) return;
    
    this._editRouteService.addPointToEditedCustomRouteData(
      point as EditedPoint,
      validation.orderIndex!,
      validation.dayIndex!
    );
    
    this._matBottomSheetService.closeAllMatBottomSheets();
  }

  /**
   * Replaces a point in the edited custom route
   */
  public replacePoint(
    pointToReplace: SuggestedPoint | EditedPoint,
    data: MatBottomSheetAddNewPointOrReplacePointData
  ): void {
    const validation = this.validateBottomSheetData(data);
    if (!validation.isValid) return;
    
    const pointToBeReplaced = data.pointToBeReplaced;
    if (!pointToBeReplaced) return;
    
    this._editRouteService.replacePointToEditedCustomRouteData(
      pointToReplace as EditedPoint,
      pointToBeReplaced as EditedPoint,
      validation.dayIndex!
    );
    
    this._matBottomSheetService.closeAllMatBottomSheets();
  }
}
